/**
 * What to TELL someone whose save was refused.
 *
 * `markRejected` has always stored a `RejectionInfo`, and nothing has ever
 * rendered it — so a refusal reached the phone, was written to IndexedDB, and
 * disappeared. The human saw a save that looked like every other save. That was
 * survivable while the only refusals were malformed input (a bug, not a race);
 * it stopped being survivable when two people started sharing a project and
 * "someone else changed this first" became a thing that happens on purpose.
 *
 * The server's own message is not the thing to show. `status conflict: decided
 * against can_do, but the item is now done` is precise, and it is written for
 * whoever is reading a log — it names internal states and says nothing about
 * what to DO. This maps the wire CODE (not the message, which is prose and will
 * change) onto a sentence with a next action in it.
 *
 * Unknown codes fall through to a generic line that is honest about the two
 * things that are always true: it did not save, and the recording is still here.
 * Never invent a specific cause for a code we do not recognise — a confident
 * wrong reason sends someone to fix the wrong thing.
 */

import type { RejectionInfo } from "./types";

/** One line, plain, with the next move in it. §4 rule 10: no acronyms, no
 * jargon — "administrator" and "validation" are not jobsite words. */
export function rejectionCopy(rejection: RejectionInfo | undefined): string {
  switch (rejection?.code) {
    case "status_conflict":
      // The one refusal that is nobody's mistake. Says what happened, and that
      // looking again is the whole fix — the current state is already on screen
      // after the next read.
      return "Someone else changed this item first, so your change wasn't saved. Open it again to see where it stands now.";
    case "project_out_of_scope":
    case "tenant_mismatch":
      return "You don't have access to that project, so this wasn't saved. Ask your project manager to add you.";
    case "invalid_token":
    case "missing_token":
      // Distinct from the above on purpose: same "you can't", different fix.
      return "Your sign-in is no longer valid, so this wasn't saved. Ask for a new code.";
    default:
      return "This didn't save. It's still here — open it to try again.";
  }
}
